import { Container } from '@tryhackme/thm-ui-components';
import styled from 'styled-components';
import { StyledGradientBg } from '../commons/commons.styles';

export const StyledContainer = styled(Container)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6rem;
  width: 100%;
  max-width: 132rem;
  margin: 0 auto 6rem;
  padding-top: 8rem;
  position: relative;
  ${StyledGradientBg};

  &::before {
    top: -50%;
    transform: translateY(-60%);
    left: 0;
    border-radius: 20rem;
  }

  @media (max-width: ${({ theme }) => theme.sizes.lg}) {
    margin: 10rem auto 8rem;
  }

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    margin: 2.4rem auto 4.6rem;
    gap: 4rem;
  }

`;

export const StyledHeroContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2.4rem;
  z-index: 1;
  position: relative;
`;

export const StyledTag = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.3rem;
  padding: 0.65rem 1.74rem 0.65rem 0.87rem;
  background: ${({ theme }) => theme.colors.secondary.light};
  border-radius: 3.26rem;

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    padding: 0.6rem 1.4rem 0.6rem 0.8rem;
    gap: 1rem;
  }
`;

export const StyledNewTag = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.22rem 0.87rem;
  background: ${({ theme }) => theme.colors.success.lightest};
  border-radius: 3.26rem;
`;

export const StyledNewTagText = styled.span`
  font-family: ${({ theme }) => theme.fonts.familyUbuntu};
  font-size: 1.3rem;
  line-height: 1.33;
  color: ${({ theme }) => theme.colors.primary.main};
  font-weight: ${({ theme }) => theme.fonts.weight.regular};

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    font-size: 1.1rem;
  }
`;

export const StyledNewTagContent = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 1.09rem;

  span {
    font-family: ${({ theme }) => theme.fonts.familySansPro};
    font-size: 1.74rem;
    color: ${({ theme }) => theme.colors.text.lighter};
    font-weight: ${({ theme }) => theme.fonts.weight.regular};

    svg {
      margin-left: 0.1rem;
      position: relative;
      top: 1px;
    }

    @media (max-width: ${({ theme }) => theme.sizes.md}) {
      font-size: 1.4rem;
    }
  }
`;

export const StyledHeading = styled.h1`
  font-family: ${({ theme }) => theme.fonts.familySansPro};
  font-size: 5.2rem;
  line-height: 1.257;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.main};
  font-weight: ${({ theme }) => theme.fonts.weight.bold};
  max-width: 116.8rem;
  margin: 0;

  @media (max-width: ${({ theme }) => theme.sizes.lg}) {
    font-size: 4.2rem;
  }

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    font-size: 3.2rem;
  }
`;

export const StyledDescription = styled.p`
  font-family: ${({ theme }) => theme.fonts.familySansPro};
  font-size: 2.2rem;
  line-height: 1.257;
  text-align: center;
  color: ${({ theme }) => theme.colors.grey[400]};
  font-weight: ${({ theme }) => theme.fonts.weight.regular};
  max-width: 72.6rem;
  margin: 0;

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    font-size: 1.8rem;
  }

  @media (max-width: ${({ theme }) => theme.sizes.sm}) {
    font-size: 1.6rem;
  }
`;

export const StyledCTAWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  width: 100%;
  margin-top: 3.6rem;

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    margin-top: 3.2rem;
  }
`;

export const StyledCTAButtons = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1.8rem;

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    flex-direction: column;
    gap: 1.8rem;
    width: 100%;
  }
`;

export const StyledChatWithSales = styled.button`
  font-family: ${({ theme }) => theme.fonts.familySansPro};
  font-size: ${({ theme }) => theme.fonts.sizes.default};
  text-align: center;
  color: ${({ theme }) => theme.colors.primary.main};
  font-weight: ${({ theme }) => theme.fonts.weight.regular};
  cursor: pointer;
  transition: opacity 0.2s ease-in-out;
  text-decoration: underline;
`;

export const StyledButton = styled.button<{ variant: 'primary' | 'secondary' }>`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2rem;
  padding: 1rem 3.6rem;
  font-family: ${({ theme }) => theme.fonts.familyUbuntu};
  font-size: 1.8rem;
  line-height: 1.149;
  font-weight: ${({ theme }) => theme.fonts.weight.regular};
  border-radius: 0.4rem;
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  ${({ variant, theme }) =>
    variant === 'primary'
      ? `
    background: ${theme.colors.primary.main};
    color: ${theme.colors.text.invert};
    border: 0.1rem solid ${theme.colors.primary.main};

    &:hover {
      background: ${theme.colors.primary.dark || theme.colors.primary.main};
      transform: translateY(-0.1rem);
      box-shadow: 0 0.4rem 1.2rem rgba(163, 234, 42, 0.3);
    }
  `
      : `
    background: transparent;
    color: ${theme.colors.text.main};
    border: 0.1rem solid ${theme.colors.primary.main};

    &:hover {
      background: rgba(163, 234, 42, 0.1);
      transform: translateY(-0.1rem);
    }
  `}

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    width: 100%;
    padding: 1.2rem 2.4rem;
    font-size: 1.6rem;
  }
`;
